import { useState, useEffect } from "react";
import { Zap, AlertTriangle, CheckCircle, Loader, RefreshCw, Clock } from "lucide-react";
import { groundOpsApi } from "@/api";

const RISK_STYLE = {
  "ON TIME":        { bar: "bg-green-500",  text: "text-green-400",  ring: "ring-green-500/30"  },
  "AT RISK":        { bar: "bg-amber-500",  text: "text-amber-400",  ring: "ring-amber-500/30"  },
  "LIKELY DELAYED": { bar: "bg-red-500",    text: "text-red-400",    ring: "ring-red-500/30"    },
};

function ConfidenceGauge({ score }) {
  const style = score >= 70 ? RISK_STYLE["ON TIME"] : score >= 40 ? RISK_STYLE["AT RISK"] : RISK_STYLE["LIKELY DELAYED"];
  const circumference = 2 * Math.PI * 40;
  const dash = (score / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center w-28 h-28" data-testid="confidence-gauge">
      <svg width="112" height="112" viewBox="0 0 112 112" className="-rotate-90">
        <circle cx="56" cy="56" r="40" fill="none" stroke="#1e3a5f" strokeWidth="10" />
        <circle
          cx="56" cy="56" r="40" fill="none"
          stroke={score >= 70 ? "#22c55e" : score >= 40 ? "#f59e0b" : "#ef4444"}
          strokeWidth="10"
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
      </svg>
      <div className="absolute text-center">
        <div className={`text-2xl font-black font-mono leading-none ${style.text}`}>{score}</div>
        <div className="text-[8px] text-slate-500 uppercase tracking-wider mt-0.5">confidence</div>
      </div>
    </div>
  );
}

export default function AIPredictionPanel({ flightId }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [fetching, setFetching]     = useState(true);

  useEffect(() => {
    groundOpsApi.getLatestPrediction(flightId)
      .then(p => { if (p && p.confidence_score !== undefined) setPrediction(p); })
      .finally(() => setFetching(false));
  }, [flightId]);

  async function runPrediction() {
    setLoading(true);
    try {
      const result = await groundOpsApi.predictDeparture(flightId);
      setPrediction(result);
    } catch { /* silent */ } finally { setLoading(false); }
  }

  const style = prediction ? (RISK_STYLE[prediction.risk_level] || RISK_STYLE["ON TIME"]) : null;

  if (fetching) return (
    <div className="bg-[#0a1828] border border-slate-700/30 rounded-xl p-5 mt-4 flex items-center gap-3 text-slate-600 text-sm">
      <Loader size={14} className="animate-spin" /> Checking for predictions...
    </div>
  );

  return (
    <div className="bg-[#0a1828] border border-slate-700/30 rounded-xl overflow-hidden mt-4" data-testid="ai-prediction-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/30">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-teal-400" />
          <span className="text-[10px] font-black uppercase tracking-widest text-teal-400">AI Departure Prediction</span>
          {prediction && (
            <span className="text-[8px] text-slate-600 font-mono ml-2">
              {new Date(prediction.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })} IST
            </span>
          )}
        </div>
        <button
          onClick={runPrediction}
          disabled={loading}
          className="flex items-center gap-1.5 text-[10px] font-bold text-teal-400 border border-teal-500/30 bg-teal-500/10 hover:bg-teal-500/20 px-2.5 py-1.5 rounded-lg transition disabled:opacity-50"
          data-testid="run-prediction-btn"
        >
          {loading ? <Loader size={9} className="animate-spin" /> : <RefreshCw size={9} />}
          {prediction ? "Re-analyse" : "Run AI Analysis"}
        </button>
      </div>

      {/* Loading state */}
      {loading && !prediction && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="relative mb-4">
            <div className="w-16 h-16 rounded-full border-2 border-teal-500/20 flex items-center justify-center">
              <Zap size={24} className="text-teal-400 animate-pulse" />
            </div>
            <div className="absolute inset-0 rounded-full border-2 border-teal-500/40 border-t-teal-400 animate-spin" />
          </div>
          <p className="text-sm text-teal-400 font-semibold">Analysing turnaround state...</p>
          <p className="text-[10px] text-slate-500 mt-1">Claude reviewing 15 tasks, vendor history, departure window</p>
        </div>
      )}

      {/* No prediction yet */}
      {!loading && !prediction && (
        <div className="flex flex-col items-center justify-center py-10 text-center text-slate-600">
          <Zap size={32} className="mb-3 opacity-20" />
          <p className="text-sm text-slate-500">No prediction yet</p>
          <p className="text-[10px] text-slate-600 mt-1">Click "Run AI Analysis" for a departure confidence score</p>
        </div>
      )}

      {/* Result */}
      {prediction && !loading && (
        <div className="p-5">
          <div className="flex items-start gap-6 mb-5">
            {/* Gauge */}
            <ConfidenceGauge score={prediction.confidence_score} />

            {/* Summary */}
            <div className="flex-1">
              <div className={`text-xs font-black uppercase tracking-widest mb-1 ${style.text}`}>
                {prediction.risk_level}
              </div>
              <div className="text-white text-sm leading-relaxed mb-2">
                {prediction.confidence_explanation}
              </div>
              {prediction.predicted_delay_mins > 0 && (
                <div className="flex items-center gap-2 text-[11px]">
                  <Clock size={10} className="text-amber-400" />
                  <span className="text-amber-400 font-bold">Predicted delay: +{prediction.predicted_delay_mins} min</span>
                </div>
              )}
            </div>
          </div>

          {/* Risk Factors */}
          {prediction.risk_factors?.length > 0 && (
            <div className="mb-4">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-2">Risk Factors</div>
              <div className="flex flex-wrap gap-2">
                {prediction.risk_factors.map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px] bg-red-500/10 border border-red-500/20 text-red-300 px-2 py-1 rounded-lg">
                    <AlertTriangle size={9} />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendation */}
          <div className="bg-teal-500/5 border border-teal-500/20 rounded-xl p-4 mb-4">
            <div className="text-[9px] uppercase tracking-widest text-teal-400 font-bold mb-1.5">OCC Recommendation</div>
            <p className="text-sm text-white font-semibold">{prediction.recommendation}</p>
          </div>

          {/* Actions */}
          {prediction.key_actions?.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-2">Action Items</div>
              <div className="space-y-1.5">
                {prediction.key_actions.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] text-slate-300">
                    <CheckCircle size={10} className="text-teal-400 flex-shrink-0 mt-0.5" />
                    {a}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
